import { useEffect, useRef, useState } from "react";
import "./MessageAudioPlayer.scss";

interface MessageAudioPlayerProps {
  audioSrc: string;
  autoPlay?: boolean;
}

// Static waveform bars — a calm center-peaked shape with a deterministic
// jitter (same visual language as the recorder/preview waveform, so a sent
// voice note reads as the same family as the composer). Fewer, chunkier bars
// than the composer since a played-back note is more compact.
const BARS = Array.from({ length: 34 }, (_, i) => {
  const t = i / 33;
  const envelope = Math.pow(Math.sin(t * Math.PI), 0.5);
  const noise = Math.sin(i * 12.9898) * 43758.5453;
  const rnd = noise - Math.floor(noise);
  return Math.max(0.22, Math.min(1, 0.3 + envelope * (0.3 + 0.7 * rnd)));
});

const formatTime = (totalSeconds: number) => {
  const safe =
    Number.isFinite(totalSeconds) && totalSeconds > 0 ? totalSeconds : 0;
  const minutes = Math.floor(safe / 60);
  const seconds = Math.floor(safe % 60);
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
};

const renderBars = () =>
  BARS.map((h, i) => (
    <span
      key={i}
      className="message-audio-bar"
      style={{ height: `${Math.round(h * 100)}%` }}
    />
  ));

// On-brand playback of a sent voice note — replaces the raw <audio controls>
// browser chrome. A play/pause button, a scrubbable waveform with a smooth
// progress fill, and the elapsed/total time. Everything is drawn in
// `currentColor`, so it inherits the surrounding message's text colour: white
// on the creator's brand bubble, dark on the flat agent side — no theming props.
const MessageAudioPlayer = ({
  audioSrc,
  autoPlay = false,
}: MessageAudioPlayerProps) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;
  // Count up while playing / after a scrub, otherwise show the full length.
  const displaySeconds = isPlaying || currentTime > 0 ? currentTime : duration;

  // The `timeupdate` event only fires ~4×/second, which makes the progress
  // fill step visibly. While playing, sample the real currentTime every frame
  // (~60fps) so the fill sweeps smoothly instead of jumping bar-to-bar.
  useEffect(() => {
    if (!isPlaying) {
      return;
    }
    let raf = 0;
    const tick = () => {
      const audio = audioRef.current;
      if (audio) {
        setCurrentTime(audio.currentTime);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isPlaying]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch(() => {
        // Autoplay/gesture failures are non-fatal.
      });
    }
  };

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) {
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const fraction = Math.min(
      1,
      Math.max(0, (e.clientX - rect.left) / rect.width),
    );
    audio.currentTime = fraction * duration;
    setCurrentTime(fraction * duration);
  };

  return (
    <div className="message-audio">
      <button
        type="button"
        className="message-audio-play"
        aria-label={isPlaying ? "Pause" : "Play"}
        onClick={togglePlay}
      >
        {isPlaying ? (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <rect x="6" y="5" width="4" height="14" rx="1.3" />
            <rect x="14" y="5" width="4" height="14" rx="1.3" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5.5v13a1 1 0 0 0 1.53.85l10-6.5a1 1 0 0 0 0-1.7l-10-6.5A1 1 0 0 0 8 5.5z" />
          </svg>
        )}
      </button>
      <div className="message-audio-wave" onClick={seek}>
        {renderBars()}
        {/* Played overlay: an exact copy of the bars, clipped from the left to
            the current progress — a continuous (sub-bar) fill that sweeps
            smoothly rather than snapping whole bars at a time. */}
        <div
          className="message-audio-fill"
          aria-hidden="true"
          style={{ ["--progress" as any]: `${progress * 100}%` }}
        >
          {renderBars()}
        </div>
      </div>
      <div className="message-audio-time">{formatTime(displaySeconds)}</div>
      <audio
        ref={audioRef}
        src={audioSrc}
        autoPlay={autoPlay}
        preload="metadata"
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          if (Number.isFinite(d)) {
            setDuration(d);
          }
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onEnded={() => {
          setIsPlaying(false);
          setCurrentTime(0);
        }}
      />
    </div>
  );
};

export default MessageAudioPlayer;
